import React from 'react';
import { type UseFormWatch } from 'react-hook-form';
import { Label } from '@/admin/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/admin/components/ui/select';
import type { Blog } from '@/admin/lib/api';
import type { BlogFormData } from '@/admin/pages/blog-detail';

interface BlogSettingsSidebarProps {
  blog: Blog;
  watch: UseFormWatch<BlogFormData>;
  onStatusChange: (status: string) => void;
  loading: boolean;
}

export const BlogSettingsSidebar: React.FC<BlogSettingsSidebarProps> = ({
  blog,
  watch,
  onStatusChange,
  loading,
}) => {
  const status = watch('status');

  if (loading) return null;

  return (
    <div className="w-64 border-l bg-card flex flex-col shrink-0">
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-5">
          {/* Status Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Status
              </h3>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="status" className="text-xs">
                Visibility
              </Label>
              <Select value={status} onValueChange={onStatusChange}>
                <SelectTrigger id="status" className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="h-px bg-border" />

          {/* System Info */}
          <div className="space-y-2">
            <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              System
            </h3>
            <dl className="text-[10px] space-y-1.5 text-muted-foreground">
              <div className="flex justify-between">
                <dt>ID</dt>
                <dd className="font-mono text-foreground/70">
                  {blog.id.slice(0, 8)}…
                </dd>
              </div>
              <div className="flex justify-between">
                <dt>Slug</dt>
                <dd
                  className="font-mono text-foreground/70 truncate max-w-[120px]"
                  title={blog.slug}
                >
                  {blog.slug}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt>Created</dt>
                <dd className="text-foreground/70">
                  {new Date(blog.created_at).toLocaleDateString()}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt>Updated</dt>
                <dd className="text-foreground/70">
                  {new Date(blog.updated_at).toLocaleDateString()}
                </dd>
              </div>
              {blog.published_at && (
                <div className="flex justify-between">
                  <dt>Published</dt>
                  <dd className="text-foreground/70">
                    {new Date(blog.published_at).toLocaleDateString()}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
};
